/**
 * Hidden-note ghosts (W2-02; DESIGN.md §4.8 "why is my note not visible?"): dashed outlines of notes Phira does not
 * draw at the playhead (`FrameDrawList.ghosts`, computed when FrameOptions.ghosts is on), with the reason on hover.
 * Clicking a ghost selects its note (selection.ts picks ghosts after drawn notes).
 */

import type { AppServices } from '../../../app/registry/types';
import type { GhostReason } from '../../../core/engine/types';
import type { NoteId } from '../../../core/model/types';
import type { OverlayDrawContext, OverlayHit, PreviewOverlayProvider } from '../../../render/types';
import { m } from '../preview.messages';
import { pointInQuad } from './gizmoMath';
import { dp, pathQuad } from './shared';
import { overlayTheme } from './theme';

const REASON_KEY: Readonly<Record<GhostReason, string>> = {
  cover: m.ghostCover,
  negativeLineAlpha: m.ghostNegativeLineAlpha,
  culled: m.ghostCulled,
  uiMoverLine: m.ghostUiMoverLine,
  beforeVisibleTime: m.ghostBeforeVisibleTime,
  offscreen: m.ghostOffscreen,
  filtered: m.ghostFiltered,
};

/** Message key explaining why a note is hidden. */
export function ghostReasonKey(r: GhostReason): string {
  return REASON_KEY[r];
}

function ghostPx(c: OverlayDrawContext, quad: readonly number[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < 4; i++) {
    const [px, py] = c.worldToPx(quad[i * 2] ?? 0, quad[i * 2 + 1] ?? 0);
    out.push(px, py);
  }
  return out;
}

/** The ghost under a point (device px), topmost (last) first. */
export function ghostAt(
  c: OverlayDrawContext,
  px: number,
  py: number,
): { readonly note: NoteId; readonly reason: GhostReason } | null {
  const ghosts = c.list.ghosts;
  for (let i = ghosts.length - 1; i >= 0; i--) {
    const g = ghosts[i];
    if (g !== undefined && pointInQuad(px, py, ghostPx(c, g.quad))) return { note: g.note, reason: g.reason };
  }
  return null;
}

export function createGhostsOverlay(services: AppServices): PreviewOverlayProvider {
  const enabled = (): boolean => services.editor.getState().preview.hiddenGhosts;
  return {
    id: 'preview.ghosts',
    order: 30,
    draw(c) {
      if (!enabled() || c.list.ghosts.length === 0) return;
      const theme = overlayTheme();
      const ctx = c.ctx;
      ctx.save();
      ctx.strokeStyle = theme.ghost;
      ctx.lineWidth = dp(c, 1.25);
      ctx.setLineDash([dp(c, 4), dp(c, 3)]);
      ctx.globalAlpha = 0.9;
      for (const g of c.list.ghosts) {
        pathQuad(ctx, ghostPx(c, g.quad));
        ctx.stroke();
      }
      ctx.restore();
    },
    hitTest(c, px, py): OverlayHit | null {
      if (!enabled()) return null;
      const g = ghostAt(c, px, py);
      if (g === null) return null;
      return {
        provider: 'preview.ghosts',
        cursor: 'help',
        data: { tooltip: { key: ghostReasonKey(g.reason) }, note: g.note },
      };
    },
  };
}
